import { Injectable } from '@angular/core'
import { RoleplayWebSocketFacadeService } from './roleplay-websocket-facade.service'
import { PersonaData } from '../persona/persona.mapping'
import { BehaviorSubject, Observable } from 'rxjs'

/**
 * RoleplaySessionService
 *
 * セッションライフサイクル管理を担当するサービス。
 *
 * **責務**:
 * - セッションID生成
 * - セッション開始・終了の調整
 * - セッション時間追跡
 * - アセスメント作成の最小時間検証（90秒）
 *
 * **依存関係**:
 * - RoleplayWebSocketFacadeService: WebSocket通信の集約
 *
 * **要件**: 1.1, 11.8
 */
@Injectable({
  providedIn: 'root',
})
export class RoleplaySessionService {
  private sessionStartTime: number = 0
  private readonly MIN_CONVERSATION_DURATION_FOR_ASSESSMENT_MS = 90000 // 90秒
  private aiFinishedPending = new BehaviorSubject<boolean>(false)

  constructor(private webSocketFacade: RoleplayWebSocketFacadeService) {}

  /**
   * セッションIDを生成
   *
   * crypto.randomUUID() が利用可能な場合はそれを使用し、
   * 利用不可能な場合はタイムスタンプとランダム文字列から生成。
   *
   * @returns 生成されたセッションID
   */
  generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }

    const timestamp = Date.now().toString(36)
    const randomStr = Math.random().toString(36).substring(2, 15)
    const randomStr2 = Math.random().toString(36).substring(2, 15)
    return `session-${timestamp}-${randomStr}-${randomStr2}`
  }

  /**
   * セッションを開始
   *
   * WebSocketファサードを通じてセッション開始を通知し、
   * セッション開始時刻を記録する。
   *
   * @param sessionId セッションID
   * @param persona ペルソナデータ
   */
  async startSession(sessionId: string, persona: PersonaData): Promise<void> {
    this.sessionStartTime = Date.now()
    this.aiFinishedPending.next(false)
    await this.webSocketFacade.startSession(sessionId, persona)
  }

  /**
   * セッションを終了
   *
   * WebSocketファサードを通じてセッション終了を通知し、
   * セッション開始時刻をリセットする。
   *
   */
  async endSession(): Promise<void> {
    await this.webSocketFacade.endSession()
    this.sessionStartTime = 0
  }

  /**
   * AI判定によるセッション終了フラグを設定
   */
  setAiFinishedPending(value: boolean): void {
    this.aiFinishedPending.next(value)
  }

  /**
   * AI判定によるセッション終了フラグの現在値を取得
   */
  getAiFinishedPending(): Observable<boolean> {
    return this.aiFinishedPending.asObservable()
  }

  /**
   * セッション開始からの経過時間を取得（ミリ秒）
   *
   * @returns 経過時間（ミリ秒）。セッションが開始されていない場合は0
   */
  getSessionDurationMs(): number {
    if (this.sessionStartTime === 0) {
      return 0
    }
    return Date.now() - this.sessionStartTime
  }

  /**
   * アセスメント作成に必要な最小時間（90秒）を満たしているかチェック
   *
   * @returns 最小時間を満たしている場合true、満たしていない場合false
   */
  isMinimumDurationMet(): boolean {
    return (
      this.getSessionDurationMs() >=
      this.MIN_CONVERSATION_DURATION_FOR_ASSESSMENT_MS
    )
  }
}
